import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { checkOfferType, formatDate } from '../../../../../shared/functions/Functions';
import { MessagesIcon, PlannerIcon, ReportingIcon } from '../../../../../shared/Icons';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { UserRoles } from '../../../../../enum/UserRoles';
import { gtmService } from '../../../../../utils/gtm.service';
import { IOfferWithCampaign } from '../../../profile/profile-view';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import DOMPurify from 'dompurify';
import PaymentInfoBlock from '../../../../../shared/components/PaymentInfoBlock';
import { PaymentTermsEnum } from '../../../../../enum/PaymenTermsEnum';

interface ITableViewOfferProps {
    offer: IOfferWithCampaign;
}

const ListViewOfferCard = ({ offer }: ITableViewOfferProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        user: { userType },
    } = useSelector((reduxState: IAllStates) => reduxState.auth);
    const isAgency = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles);
    const hasBadge = offer?.isLongTerm || offer?.isAddonsOnly || offer?.isOnTop;
    const navigateHandler = (route: string, campaignId: number, publishFrom?: string) => {
        navigate(route, {
            state: {
                campaignId,
                publishFrom,
            },
        });
    };

    const navigateToCampaignOverview = (id: number) => {
        isAgency ? navigate(`/agency/campaign/${id}/overview`) : navigate(`/influencer/campaign/${id}/overview`);
        gtmService('select_content');
    };

    return (
        <CardLayout classBody="contract-list-card px-3 py-0">
            <div className="image-wrapper">
                <CustomImageHandler
                    thumbnailPath={offer?.campaign?.campaignImages?.[0]?.thumbnail}
                    photoPath={offer?.campaign?.campaignImages?.[0]?.thumbnail}
                    altTag="Campaign Image"
                />
                {hasBadge && (
                    <span className={`status-badge ${hasBadge ? 'linked' : 'long'}`}>
                        {t(
                            `brand.campaign.briefing.overview_card.${checkOfferType(
                                offer?.isLongTerm,
                                offer?.isOnTop,
                                offer?.isAddonsOnly
                            )}`
                        )}
                    </span>
                )}
            </div>
            <div className="card-content pl-3">
                <h5
                    className="mb-0 cursor-pointer"
                    onClick={() => navigateToCampaignOverview(offer?.campaign?.id ?? 0)}
                >
                    {offer?.campaign?.title}
                </h5>
                <p className="mb-0 text-primary">
                    {offer?.campaign?.company?.displayName || '-'}

                    {(offer?.isLongTerm || offer?.isAddonsOnly) && (
                        <>
                            {' '}
                            <span
                                className="text-muted cursor-pointer"
                                onClick={() => navigateToCampaignOverview(offer?.campaign?.parent?.id ?? 0)}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        `| ${t('brand.campaign.book.offer.part_of_name', {
                                            campaign: offer?.campaign?.parent?.title ?? '-',
                                        })}`
                                    ),
                                }}
                            />
                        </>
                    )}
                </p>
            </div>

            <div className="card-info">
                <div className="info-block">
                    <div className="info-main">
                        {formatDate(offer?.campaign?.publishFrom ?? '') +
                            ' - ' +
                            formatDate(offer?.campaign?.publishTo)}
                    </div>
                    <div>{t('influencer.explore.publicationPeriod')}</div>
                </div>
                <div className="info-block">
                    <PaymentInfoBlock
                        main={t(
                            `brand.campaign.briefing.step8.payment_terms_type.${
                                offer?.campaign?.paymentTerms === PaymentTermsEnum.OTHER
                                    ? 'custom'
                                    : offer?.campaign?.paymentTerms
                            }`
                        )}
                        label={t('brand.campaign.briefing.step8.payment_terms')}
                        showTooltip={offer?.campaign?.paymentTerms === PaymentTermsEnum.OTHER}
                        tooltipContent={offer?.campaign?.paymentTermsCustom ?? ''}
                        tooltipId="payment_tooltip"
                        mainClassName="info-main"
                    />
                </div>
                <div className="info-block">
                    <div
                        className={`info-main ${
                            !offer?.campaign?.isCharity && 'euro-symbol'
                        } text-primary font-weight-extra-bold`}
                    >
                        {offer?.campaign?.isCharity
                            ? t('brand.account.price_list_modal.barter_deals')
                            : priceSplitter(Math.round(offer?.price ?? 0))}
                    </div>
                    <div>
                        {offer?.isLongTerm
                            ? t('brand.campaign.book.offer.modal.value')
                            : t('brand.campaign.book.offer.modal.price')}
                    </div>
                </div>
                <div className="info-block">
                    <div className="info-main">{offer?.totalNbPosts ?? 0}</div>
                    <div>{t('influencer.explore.contentPieces')}</div>
                </div>
                <div className="info-block">
                    <div className="d-flex align-items-center justify-content-between">
                        <div
                            onClick={() =>
                                navigateHandler(
                                    isAgency ? '/agency/planner' : '/influencer/planner',
                                    offer?.campaign?.id,
                                    offer?.campaign?.publishFrom
                                )
                            }
                            className="cursor-pointer  d-flex align-items-center flex-column justify-content-center position-relative"
                        >
                            <PlannerIcon width="20" height="20" />
                            <span className="font-10">{t('influencer.campaign.tabs.planner')}</span>
                            {offer?.redDots?.plannerDot && <div id="bullet" className="offer-bullet notify-dot" />}
                        </div>
                        <div
                            onClick={() =>
                                navigateHandler(
                                    isAgency ? '/agency/reporting' : '/influencer/reporting',
                                    offer?.campaign?.id
                                )
                            }
                            className="cursor-pointer   d-flex align-items-center flex-column justify-content-center position-relative"
                        >
                            <ReportingIcon width="20" height="20" />
                            <span className="font-10">{t('influencer.campaign.tabs.reporting')}</span>
                            {offer?.redDots?.reportingDot && <div id="bullet" className="offer-bullet notify-dot" />}
                        </div>
                        <div
                            onClick={() =>
                                navigateHandler(
                                    isAgency ? '/agency/messages' : '/influencer/messages',
                                    offer?.campaign?.id
                                )
                            }
                            className="cursor-pointer d-flex align-items-center flex-column justify-content-center position-relative"
                        >
                            <MessagesIcon width="20" height="20" />
                            <span className="font-10">{t('influencer.campaign.tabs.messages')}</span>
                            {offer?.redDots?.messagesDot && <div id="bullet" className="offer-bullet notify-dot" />}
                        </div>
                    </div>
                </div>
            </div>
        </CardLayout>
    );
};

export default ListViewOfferCard;
